import { Rule, Transition, StateType, Page, Question, RuleEvent, RuleAction, ButtonRuleType, Button } from "../../common/models/flow.dto";
import { LunaCodeNode } from "../../common/models/rules.model";
import { DomainValue } from "../../common/models/document-tag.model";
import { TagSelector } from "../models/file-tag-configuration.model";
import { IUploadFile } from "../../common/services/document-management.service";

export class RuleEditorModel implements Rule {

  action: RuleAction | null;
  buttonRuleType: ButtonRuleType | null;
  contextTarget: string = '';
  expression: string = '';
  name: string;
  parseTree: LunaCodeNode | null = null;
  ruleId: string;
  sourceButton: Button | null;
  sourcePage: Page | null;
  sourceQuestion: Question | null;
  sourceTransition: Transition | null;
  trigger: RuleEvent | null;

  constructor(c: Rule, action: RuleAction | null, trigger: RuleEvent | null, buttonRule: ButtonRuleType | null) {
    Object.assign(this, c);
    if (!this.contextTarget) this.contextTarget = '';
    this.action = action;
    this.trigger = trigger;
    this.buttonRuleType = buttonRule;
  }

  setSource(source: Transition | Page | Question | Button) {
    if (this.isButton(source)) {
      this.sourceButton = source as Button;
    } else if (this.isTransition(source)) {
      this.sourceTransition = source as Transition;
    } else if (this.isPage(source)) {
      this.sourcePage = source as Page;
    } else if (this.isQuestion(source)) {
      this.sourceQuestion = source as Question;
    }
  }

  public isButton(source: any): source is Button {
    return source.position !== undefined;
  }

  public isPage(source: any): source is Page {
    return source.pageNumber !== undefined;
  }

  public isQuestion(source: any): source is Question {
    return source.validationRules !== undefined;
  }

  public isTransition(source: any): source is Transition {
    return source.outgoingRules !== undefined;
  }

}

export function deepClone(obj): any {

  if (typeof (obj) !== "object" || obj === null) return obj;

  let clone: any[] | {} & any;
  if (Array.isArray(obj)) {
    clone = obj.slice();
  } else {
    clone = Object.assign({}, obj);
  }

  const keys = Object.keys(clone);
  for (let i = 0; i < keys.length; i++) {
    clone[keys[i]] = deepClone(clone[keys[i]]);
  }

  return clone;
}

export function areEqual(obj1: any, obj2: any): boolean {
    if (obj1 === obj2) return true;
    if (!(obj1 instanceof Object) || !(obj2 instanceof Object)) return false;
    if (obj1.constructor !== obj2.constructor) return false;
    for (const prop in obj1) {
        if (!obj1.hasOwnProperty(prop)) continue;
        if (!obj2.hasOwnProperty(prop)) {
            return false;
        }
        if (obj1[prop] === obj2[prop]) continue;
        if (typeof (obj1[prop]) !== 'object') {
            return false;
        }
    }
    for (const prop in obj2) {
        if (obj2.hasOwnProperty(prop) && !obj1.hasOwnProperty(prop)) {
            return false;
        }
    }
    return true;
}

export function convertToPlainText(html: string): string {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
}


export interface PdfViewerConfig {
    pdfDocument: string;
    pdfOptions: TemplatePdfOptions;
    documentType: DomainValue;
    tags: TagSelector[];
}

export interface TemplatePdfOptions {
    addStaticDocuments: boolean;
    appendStaticDocumentsFirst: boolean;
    customHeader: CustomHeader;
    documentTypeContext: string;
    landscape: boolean;
    marginOptions: TemplateMarginOptions;
    mergeDocuments: boolean;   
    paperFormat: string; 
    staticFiles: IUploadFile[];
    useDefaults: boolean;
    useCustomHeader: boolean;    
}

export interface TemplateMarginOptions {
    top: number;
    bottom: number;
    left: number;
    right: number;
}

export interface CustomHeader {
    headerText: string;
    compileAsExpression: boolean;
    expressionRule: Rule;
}

export enum TransitionEditorEventType {
    NameChanged,
    FormInserted
}

export interface TransitionEditorEvent {
    eventType: TransitionEditorEventType;
    transitionName?: string;
    formType?: StateType;
    cloneForm?: boolean;
}